/**
 * useChat Hook
 * Manages live chat state and Socket.IO connection for end users
 * Consistent with Phase 3 admin chat hook patterns
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import io, { Socket } from 'socket.io-client';
import { ILiveChatConversation, ILiveChatMessage, IImageAttachment, UseChatReturn } from '../types/liveChat';
import { liveChatAPI } from '../services/api/liveChat';

const SOCKET_URL = import.meta.env.VITE_SOCKET_URL || 'http://localhost:5000';

/**
 * Hook for managing live chat in end-user widget
 * - Socket.IO connection with JWT auth
 * - State management for conversation and messages
 * - Real-time event handlers
 * - Message sending and conversation management
 */
export const useChat = (isOpen: boolean = true): UseChatReturn => {
  const [conversation, setConversation] = useState<ILiveChatConversation | null>(null);
  const [messages, setMessages] = useState<ILiveChatMessage[]>([]);
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [unreadCount, setUnreadCount] = useState(0);

  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttemptsRef = useRef(5);

  /**
   * Track which conversation rooms we're already joined
   */
  const joinedRoomsRef = useRef<Set<string>>(new Set());

  /**
   * Track whether chat panel is open (for use in closures)
   */
  const isPanelOpenRef = useRef(isOpen);

  /**
   * Update the ref whenever isOpen changes
   */
  useEffect(() => {
    isPanelOpenRef.current = isOpen;
  }, [isOpen]);

  /**
   * Initialize Socket.IO connection
   */
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    const newSocket = io(SOCKET_URL, {
      auth: { token },
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 30000,
      reconnectionAttempts: 5,
      timeout: 20000,
      // polling first so connection survives Render cold-starts, upgrades to websocket after
      transports: ['polling', 'websocket']
    });

    // Connection events
    newSocket.on('connect', async () => {
      console.log('[WEB CHAT SOCKET] Connected with socket ID:', newSocket.id);
      setIsConnected(true);
      setError(null);
      reconnectAttemptsRef.current = 0;
      // Clear joined rooms on reconnect
      joinedRoomsRef.current.clear();
      
      // Subscribe to global user room to receive messages even before panel opens
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      if (user._id) {
        newSocket.emit('chat:subscribe_to_user', { userId: user._id }, (response: any) => {
          if (response?.success) {
            console.log('[WEB CHAT SOCKET] Successfully subscribed to user messages room');
          } else {
            console.warn('[WEB CHAT SOCKET] Failed to subscribe to user messages:', response?.error);
          }
        });
        
        // Fetch active conversation on connect to be ready for incoming messages
        // This ensures we have context when messages arrive even if user hasn't opened the panel
        try {
          const activeConv = await liveChatAPI.getActiveConversation().catch(() => null);
          if (activeConv && !conversation) {
            console.log('[WEB CHAT SOCKET] Pre-loaded active conversation:', activeConv._id);
            setConversation(activeConv);
            // Also pre-load messages for this conversation
            const { messages: preloadedMessages } = await liveChatAPI.getMessages(activeConv._id, 1, 50).catch(() => ({ messages: [] }));
            if (preloadedMessages.length > 0) {
              setMessages(preloadedMessages);
              console.log('[WEB CHAT SOCKET] Pre-loaded', preloadedMessages.length, 'messages');
            }
          }
        } catch (err) {
          console.debug('[WEB CHAT SOCKET] Pre-load optional, user will load conversation on panel open');
        }
      }
    });

    newSocket.on('disconnect', (reason) => {
      console.log('Chat socket disconnected:', reason);
      setIsConnected(false);
    });

    newSocket.on('connect_error', (error) => {
      console.error('Chat socket connection error:', error);
      setError('Connection error. Retrying...');
    });

    /**
     * Real-time message received from other participants
     * Socket broadcasts only include messages from OTHER senders (not the current user)
     * Current user's messages are received via callback when sending
     * This handler works even when chat panel is closed (subscribes to user room)
     */
    newSocket.on('chat:message_received', async (message: ILiveChatMessage) => {
      console.log('[WEB CHAT MESSAGE RECEIVED]', {
        messageId: message._id,
        conversationId: message.conversation,
        senderType: message.senderType,
        senderId: message.sender,
        senderName: message.senderName,
        contentPreview: message.content?.substring(0, 50),
        isPanelOpen: isPanelOpenRef.current,
        hasConversation: !!conversation,
        timestamp: new Date().toISOString()
      });
      
      // If we don't have a conversation yet but received a message, fetch the conversation
      // This ensures we can show the unread indicator even if the user never opened the panel
      if (!conversation) {
        try {
          console.log('[WEB CHAT MESSAGE RECEIVED] No conversation loaded yet, fetching conversation:', message.conversation);
          const conv = await liveChatAPI.getConversation(message.conversation);
          setConversation(conv);
          console.log('[WEB CHAT MESSAGE RECEIVED] Conversation loaded:', conv._id);
        } catch (err) {
          console.error('[WEB CHAT MESSAGE RECEIVED] Error fetching conversation:', err);
        }
      }
      
      setMessages((prev) => {
        // Simple deduplication: check if message ID already exists
        // Since we use callback pattern for sender's messages, this should prevent duplicates
        const messageExists = prev.some((m) => m._id === message._id);
        
        if (messageExists) {
          console.log('[WEB CHAT DEDUP] Message already exists, skipping duplicate:', message._id);
          return prev;
        }
        
        console.log('[WEB CHAT STATE UPDATE] Adding new message:', message._id, 'Total before:', prev.length, 'Total after:', prev.length + 1);
        return [...prev, message];
      });
      
      // Mark as read only if chat panel is open AND window has focus
      if (isPanelOpenRef.current && document.hasFocus()) {
        console.log('[WEB CHAT AUTO READ] Chat panel is open and window focused, marking messages as read');
        markMessagesAsRead();
      } else if (!isPanelOpenRef.current) {
        console.log('[WEB CHAT UNREAD] Chat panel is closed, message will appear as unread indicator');
      }
    });

    /**
     * Typing indicator
     */
    newSocket.on('chat:typing', ({ userId, userName }: { userId: string; userName: string }) => {
      console.log(`${userName} is typing...`);
      setIsTyping(true);
    });

    newSocket.on('chat:typing_stopped', ({ userId }: { userId: string }) => {
      setIsTyping(false);
    });

    /**
     * Conversation status changed
     */
    newSocket.on('chat:conversation_status_changed', (data: { status: string; conversationId: string }) => {
      console.log('Conversation status changed:', data.status);
      if (conversation && conversation._id === data.conversationId) {
        setConversation((prev) => prev ? { ...prev, status: data.status as any } : null);
      }
    });

    /**
     * Admin assigned to conversation
     */
    newSocket.on('chat:admin_assigned', (data: { adminName: string; conversationId: string }) => {
      console.log(`Admin ${data.adminName} assigned to conversation`);
      if (conversation && conversation._id === data.conversationId) {
        setConversation((prev) => prev ? { ...prev, status: 'assigned' } : null);
      }
    });

    /**
     * Message read receipt
     */
    newSocket.on('chat:message_read', (data: { messageId: string; readAt: Date }) => {
      console.log('Message read:', data.messageId);
      setMessages((prev) =>
        prev.map((msg) =>
          msg._id === data.messageId
            ? { ...msg, status: 'read', readAt: data.readAt }
            : msg
        )
      );
    });

    /**
     * Error event
     */
    newSocket.on('error', (errorMsg: string) => {
      console.error('Chat error:', errorMsg);
      setError(errorMsg);
    });

    setSocket(newSocket);

    return () => {
      newSocket.close();
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, []);

  /**
   * Watch for unread messages
   */
  useEffect(() => {
    const unread = messages.filter((msg) => msg.status !== 'read' && msg.senderType !== 'user').length;
    console.log('[WEB CHAT UNREAD COUNT]', {
      unreadCount: unread,
      totalMessages: messages.length,
      isPanelOpen: isOpen,
      timestamp: new Date().toISOString()
    });
    setUnreadCount(unread);
  }, [messages, isOpen]);

  /**
   * Mark all messages as read when chat panel opens
   */
  useEffect(() => {
    if (!isOpen || !conversation || messages.length === 0) return;

    const unreadMessages = messages.filter((msg) => msg.status !== 'read' && msg.senderType !== 'user');
    
    if (unreadMessages.length === 0) return;

    console.log('[WEB CHAT OPEN HANDLER] Chat panel opened with', unreadMessages.length, 'unread messages, marking as read');
    
    // Mark as read via API
    (async () => {
      try {
        const unreadMessageIds = unreadMessages.map((msg) => msg._id);
        await liveChatAPI.markMessagesAsRead(conversation._id, unreadMessageIds);
        
        // Update local state to reflect read status
        setMessages((prev) =>
          prev.map((msg) =>
            unreadMessageIds.includes(msg._id)
              ? { ...msg, status: 'read' }
              : msg
          )
        );
      } catch (err) {
        console.error('[WEB CHAT OPEN HANDLER] Error marking messages as read:', err);
      }
    })();
  }, [isOpen, conversation]);

  /**
   * Join conversation room and track it
   */
  const joinConversationRoom = useCallback(
    (conversationId: string): Promise<void> => {
      return new Promise((resolve, reject) => {
        console.log('[WEB CHAT JOIN] Attempting to join conversation room:', conversationId, 'Socket connected:', socket?.connected);
        
        if (!socket?.connected) {
          console.warn('[WEB CHAT JOIN] Socket not connected, cannot join room');
          reject(new Error('Socket not connected'));
          return;
        }

        // Check if already joined
        if (joinedRoomsRef.current.has(conversationId)) {
          console.log('[WEB CHAT JOIN] Already joined room:', conversationId);
          resolve();
          return;
        }

        socket.emit('chat:join_conversation', {
          conversationId,
          userId: (JSON.parse(localStorage.getItem('user') || '{}'))._id
        }, (response: any) => {
          if (response?.success) {
            joinedRoomsRef.current.add(conversationId);
            console.log('[WEB CHAT JOIN] Successfully joined conversation room:', conversationId, 'Total joined rooms:', joinedRoomsRef.current.size);
            resolve();
          } else {
            console.error('[WEB CHAT JOIN] Failed to join conversation room:', conversationId, 'Response:', response);
            reject(new Error('Failed to join conversation room'));
          }
        });

        // Timeout fallback (5s)
        setTimeout(() => {
          if (!joinedRoomsRef.current.has(conversationId)) {
            console.warn('[WEB CHAT JOIN] Join conversation timeout for:', conversationId);
            reject(new Error('Join conversation timeout'));
          }
        }, 5000);
      });
    },
    [socket]
  );

  /**
   * Start new conversation
   */
  const startConversation = useCallback(
    async (subject?: string, message?: string) => {
      try {
        setIsLoading(true);
        setError(null);
        
        console.log('[WEB CHAT START] Starting new conversation, Socket connected:', socket?.connected, 'Socket ID:', socket?.id);

        // Check if conversation already exists
        let conv = await liveChatAPI.getActiveConversation();

        if (!conv) {
          // Create new conversation
          conv = await liveChatAPI.createConversation(subject, message);
        }

        setConversation(conv);
        console.log('[WEB CHAT START] Conversation loaded/created:', conv._id);
        
        // Fetch messages
        const { messages: fetchedMessages } = await liveChatAPI.getMessages(conv._id, 1, 50);
        setMessages(fetchedMessages);
        console.log('[WEB CHAT START] Fetched', fetchedMessages.length, 'messages for conversation:', conv._id);

        // Join socket room and wait for confirmation before allowing sends
        if (socket?.connected) {
          console.log('[WEB CHAT START] Joining conversation room:', conv._id);
          await joinConversationRoom(conv._id);
          console.log('[WEB CHAT START] Successfully joined room. Should listen to: conversation_' + conv._id);
        }
      } catch (err: any) {
        console.error('[WEB CHAT START] Error starting conversation:', err);
        setError(err.message || 'Failed to start conversation');
      } finally {
        setIsLoading(false);
      }
    },
    [socket, joinConversationRoom]
  );

  /**
   * Get conversation
   */
  const getConversation = useCallback(
    async (conversationId: string) => {
      try {
        setIsLoading(true);
        setError(null);
        
        console.log('[WEB CHAT GET] Fetching conversation:', conversationId, 'Socket connected:', socket?.connected, 'Socket ID:', socket?.id);

        const conv = await liveChatAPI.getConversation(conversationId);
        setConversation(conv);
        console.log('[WEB CHAT GET] Conversation fetched:', conversationId);

        // Fetch messages
        const { messages: fetchedMessages } = await liveChatAPI.getMessages(conversationId, 1, 50);
        setMessages(fetchedMessages);
        console.log('[WEB CHAT GET] Fetched', fetchedMessages.length, 'messages for conversation:', conversationId);

        // Join socket room and wait for confirmation
        if (socket?.connected) {
          console.log('[WEB CHAT GET] Joining conversation room:', conversationId);
          await joinConversationRoom(conversationId);
          console.log('[WEB CHAT GET] Successfully joined room. Should listen to: conversation_' + conversationId);
        }
      } catch (err: any) {
        console.error('[WEB CHAT GET] Error fetching conversation:', err);
        setError(err.message || 'Failed to fetch conversation');
      } finally {
        setIsLoading(false);
      }
    },
    [socket, joinConversationRoom]
  );

  /**
   * Send message
   */
  const sendMessage = useCallback(
    async (content: string, imageAttachment?: IImageAttachment) => {
      if (!conversation) {
        setError('No conversation started');
        return;
      }

      if (!content.trim() && !imageAttachment) {
        return;
      }

      try {
        setError(null);
        
        console.log('[WEB CHAT SEND] Starting message send, Conversation:', conversation._id, 'Socket connected:', socket?.connected);

        // Ensure we're joined to the conversation room
        if (!joinedRoomsRef.current.has(conversation._id)) {
          if (socket?.connected) {
            console.log('[WEB CHAT SEND] Not in room, joining conversation:', conversation._id);
            await joinConversationRoom(conversation._id);
          }
        }

        // Emit via Socket.IO (real-time) if connected
        if (socket?.connected) {
          // Create optimistic message for immediate UI display
          const tempId = `temp-${Date.now()}-${Math.random()}`;
          const optimisticMessage: ILiveChatMessage = {
            _id: tempId,
            conversation: conversation._id,
            content,
            type: imageAttachment ? 'image' : 'text',
            sender: (JSON.parse(localStorage.getItem('user') || '{}'))._id,
            senderType: 'user',
            senderName: (JSON.parse(localStorage.getItem('user') || '{}'))?.name || 'You',
            senderAvatar: (JSON.parse(localStorage.getItem('user') || '{}'))?.avatar,
            imageAttachment,
            status: 'sent',
            deliveredAt: new Date(),
            edited: false,
            createdAt: new Date(),
            updatedAt: new Date()
          };

          // Add optimistic message to state immediately for better UX
          console.log('[WEB CHAT SEND] Adding optimistic message:', tempId);
          setMessages((prev) => {
            console.log('[WEB CHAT SEND] Optimistic message added. Total before:', prev.length, 'Total after:', prev.length + 1);
            return [...prev, optimisticMessage];
          });

          // Send via socket
          console.log('[WEB CHAT SEND] Emitting chat:send_message for conversation:', conversation._id, 'Type:', imageAttachment ? 'image' : 'text');
          socket.emit('chat:send_message', {
            conversationId: conversation._id,
            content,
            type: imageAttachment ? 'image' : 'text',
            imageAttachment
          }, (response: any) => {
            if (!response?.success) {
              console.error('[WEB CHAT SEND] Failed to send message:', response?.error);
              // Remove optimistic message on failure
              setMessages((prev) => prev.filter((m) => m._id !== tempId));
              setError(response?.error || 'Failed to send message');
            } else if (response?.message) {
              // Replace optimistic message with real one from server
              const realMessage = response.message;
              console.log('[WEB CHAT SEND] Message sent successfully. Server returned message ID:', realMessage._id);
              setMessages((prev) =>
                prev.map((m) =>
                  m._id === tempId ? { ...realMessage, status: 'delivered' } : m
                )
              );
            }
          });
        } else {
          // Fallback to HTTP if socket disconnected
          console.log('[WEB CHAT SEND] Socket disconnected, using HTTP fallback for conversation:', conversation._id);
          const response = await liveChatAPI.sendMessage(
            conversation._id,
            content,
            imageAttachment ? 'image' : 'text',
            imageAttachment
          );
          
          console.log('[WEB CHAT SEND] HTTP message sent with ID:', response._id);
          // Add received message to state
          setMessages((prev) => {
            const messageExists = prev.some((m) => m._id === response._id);
            if (messageExists) {
              console.log('[WEB CHAT SEND] Message already in state, skipping duplicate');
              return prev;
            }
            console.log('[WEB CHAT SEND] Adding HTTP message. Total before:', prev.length, 'Total after:', prev.length + 1);
            return [...prev, response];
          });
        }
      } catch (err: any) {
        console.error('[WEB CHAT SEND] Error sending message:', err);
        setError(err.message || 'Failed to send message');
      }
    },
    [conversation, socket, joinConversationRoom]
  );

  /**
   * Close conversation
   */
  const closeConversation = useCallback(async () => {
    if (!conversation) return;

    try {
      setError(null);
      await liveChatAPI.closeConversation(conversation._id);
      setConversation((prev) => (prev ? { ...prev, status: 'closed' } : null));
    } catch (err: any) {
      console.error('Error closing conversation:', err);
      setError(err.message || 'Failed to close conversation');
    }
  }, [conversation]);

  /**
   * Rate conversation
   */
  const rateConversation = useCallback(
    async (score: number, feedback?: string) => {
      if (!conversation) return;

      try {
        setError(null);
        await liveChatAPI.rateConversation(conversation._id, score, feedback);
      } catch (err: any) {
        console.error('Error rating conversation:', err);
        setError(err.message || 'Failed to rate conversation');
      }
    },
    [conversation]
  );

  /**
   * Mark messages as read
   */
  const markMessagesAsRead = useCallback(async () => {
    if (!conversation) return;

    try {
      const unreadMessageIds = messages
        .filter((msg) => msg.status !== 'read' && msg.senderType !== 'user')
        .map((msg) => msg._id);

      if (unreadMessageIds.length === 0) return;

      await liveChatAPI.markMessagesAsRead(conversation._id, unreadMessageIds);

      // Update local state
      setMessages((prev) =>
        prev.map((msg) =>
          unreadMessageIds.includes(msg._id)
            ? { ...msg, status: 'read', readAt: new Date() }
            : msg
        )
      );
    } catch (err: any) {
      console.error('Error marking messages as read:', err);
    }
  }, [conversation, messages]);

  /**
   * Set typing indicator with debounce
   */
  const setIsTypingDebounced = useCallback((typing: boolean) => {
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    if (socket?.connected && conversation) {
      socket.emit('chat:typing', {
        conversationId: conversation._id,
        isTyping: typing
      });
    }

    if (typing) {
      typingTimeoutRef.current = setTimeout(() => {
        if (socket?.connected && conversation) {
          socket.emit('chat:typing', {
            conversationId: conversation._id,
            isTyping: false
          });
        }
      }, 3000);
    }
  }, [socket, conversation]);

  return {
    conversation,
    messages,
    socket,
    isConnected,
    isLoading,
    isTyping,
    error,
    unreadCount,
    startConversation,
    sendMessage,
    closeConversation,
    rateConversation,
    getConversation,
    markMessagesAsRead,
    setIsTyping: setIsTypingDebounced
  };
};